import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface User {
  id: string;
  name: string;
  email: string;
  role: "superadmin" | "administrator" | "operation_manager";
  created_at: string;
}

interface UsersManagementProps {
  currentUserId: string;
}

const roleLabels: Record<string, string> = {
  superadmin: "Superadmin",
  administrator: "Administrator",
  operation_manager: "Operation Manager",
};

const roleColors: Record<string, string> = {
  superadmin: "bg-amber-100 text-amber-700",
  administrator: "bg-emerald-100 text-emerald-700",
  operation_manager: "bg-sky-100 text-sky-700",
};

export default function UsersManagement({ currentUserId }: UsersManagementProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resettingId, setResettingId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState<"success" | "error">("success");

  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "operation_manager" as User["role"],
    password: "",
  });

  const [resetForm, setResetForm] = useState<Record<string, string>>({});

  const showFeedback = (msg: string, type: "success" | "error" = "success") => {
    setFeedback(msg);
    setFeedbackType(type);
    setTimeout(() => setFeedback(""), 4000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("users")
      .select("id, name, email, role, created_at")
      .order("created_at", { ascending: false });
    if (!error && data) {
      setUsers(data as User[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      showFeedback("Please fill in all required fields.", "error");
      return;
    }
    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-user", {
        body: {
          name: form.name.trim(),
          email: form.email.trim(),
          role: form.role,
          password: form.password,
        },
      });
      if (error) {
        showFeedback(error.message || "Failed to create user.", "error");
      } else if (data?.error) {
        showFeedback(data.error, "error");
      } else {
        showFeedback(`User "${form.name}" created successfully.`);
        setForm({ name: "", email: "", role: "operation_manager", password: "" });
        fetchUsers();
      }
    } catch (err: any) {
      showFeedback(err.message || "Failed to create user.", "error");
    }
    setCreating(false);
  };

  const handleResetPassword = async (userId: string, newPassword: string) => {
    if (!newPassword || newPassword.length < 6) {
      showFeedback("Password must be at least 6 characters.", "error");
      return;
    }
    setResettingId(userId);
    try {
      const { data, error } = await supabase.functions.invoke("reset-password", {
        body: { userId, newPassword },
      });
      if (error) {
        showFeedback(error.message || "Failed to reset password.", "error");
      } else if (data?.error) {
        showFeedback(data.error, "error");
      } else {
        showFeedback("Password updated successfully.");
        setResetForm((prev) => ({ ...prev, [userId]: "" }));
      }
    } catch (err: any) {
      showFeedback(err.message || "Failed to reset password.", "error");
    }
    setResettingId(null);
  };

  return (
    <div>
      <div className="px-6 py-5 border-b border-stone-100">
        <h2 className="text-sm font-bold text-stone-800">User Accounts</h2>
        <p className="text-xs text-stone-400 mt-0.5">
          Create new admin accounts and manage existing user passwords
        </p>
      </div>

      {feedback && (
        <div
          className={`mx-6 mt-5 p-3 rounded-xl text-xs font-medium border ${
            feedbackType === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-red-50 border-red-200 text-red-600"
          }`}
        >
          <div className="flex items-center gap-2">
            <i
              className={`${
                feedbackType === "success" ? "ri-check-line" : "ri-error-warning-line"
              } text-sm`}
            ></i>
            {feedback}
          </div>
        </div>
      )}

      {/* Create new user form */}
      <div className="px-6 py-5 flex flex-col gap-5">
        <div className="bg-stone-50 border border-stone-100 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-stone-800 mb-4 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-user-add-line text-primary"></i>
            </div>
            Create New User
          </h3>
          <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                Full Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Jane Doe"
                required
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                Email Address
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="name@example.com"
                required
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                Role
              </label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value as User["role"] })}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-700 cursor-pointer"
              >
                <option value="superadmin">Superadmin — Full access + user management</option>
                <option value="administrator">Administrator — Full access</option>
                <option value="operation_manager">Operation Manager — Articles, Projects, Interns</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">
                Password
              </label>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min 6 characters"
                required
                minLength={6}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
              />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <button
                type="submit"
                disabled={creating}
                className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
              >
                {creating ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-user-add-line"></i>
                )}
                {creating ? "Creating..." : "Create User"}
              </button>
            </div>
          </form>
        </div>

        {/* Users list */}
        <div className="border border-stone-100 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 bg-stone-50 border-b border-stone-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-stone-800 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-team-line text-primary"></i>
              </div>
              All Users ({users.length})
            </h3>
            <button
              onClick={fetchUsers}
              className="text-xs text-stone-500 hover:text-primary transition-colors cursor-pointer flex items-center gap-1"
            >
              <i className="ri-refresh-line"></i> Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-primary text-xl"></i>
            </div>
          ) : users.length === 0 ? (
            <div className="px-5 py-10 text-center text-xs text-stone-400">
              <i className="ri-user-search-line text-2xl mb-2 text-stone-300 block"></i>
              No users found in the system.
            </div>
          ) : (
            <div className="flex flex-col">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="px-5 py-4 border-b border-stone-100 last:border-0 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-stone-100 rounded-full text-xs font-bold text-stone-600 shrink-0">
                      {u.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-stone-800">{u.name}</p>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            roleColors[u.role]
                          }`}
                        >
                          {roleLabels[u.role]}
                        </span>
                        {u.id === currentUserId && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-stone-200 text-stone-600">
                            You
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-stone-400 mt-0.5">{u.email}</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                      type="text"
                      value={resetForm[u.id] || ""}
                      onChange={(e) =>
                        setResetForm((prev) => ({ ...prev, [u.id]: e.target.value }))
                      }
                      placeholder="New password"
                      className="px-3 py-2 rounded-lg border border-stone-200 text-xs text-stone-800 placeholder-stone-400 focus:outline-none focus:border-primary"
                    />
                    <button
                      onClick={() => handleResetPassword(u.id, resetForm[u.id] || "")}
                      disabled={resettingId === u.id || !resetForm[u.id]?.trim()}
                      className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium rounded-lg hover:bg-amber-100 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-1.5"
                    >
                      {resettingId === u.id ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <i className="ri-key-2-line"></i>
                      )}
                      {resettingId === u.id ? "Updating..." : "Reset Password"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}